import { Component, OnInit, OnDestroy, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ActivatedRoute } from '@angular/router';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { TranslatePipe } from '@ngx-translate/core';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { AvatarModule } from 'primeng/avatar';
import { DividerModule } from 'primeng/divider';
import { ButtonModule } from 'primeng/button';
import { CustomTableComponent, DataColumnType, ICustomTable, ButtonColumnType } from 'lib-base';
import { SisbenConsultaService} from '../../../services/sisben/sisben-consulta.service';
import { SisbenNucleoFamiliarService } from '../../../services/sisben/sisben-nucleo-familiar.service';
import { DatasetVisualizarDocumentosComponent } from '../../configuracion/datasets-dinamicos/dataset-visualizar-documentos.component';
import { NucleoFamiliarHijosComponent } from './nucleo-familiar-hijos.component';
import { JSONParser } from '@amcharts/amcharts5';
import { TranslateService } from '@ngx-translate/core';
import { firstValueFrom, forkJoin } from 'rxjs';
import { IGetNucleoFamiliar, IGetPerfilUsuario } from '../../../shared/interfaces/sisben/sisben';

@Component({
  selector: 'app-perfil-sisben',
  standalone: true,
  imports: [
    CommonModule,
    TranslatePipe,
    ProgressSpinnerModule,
    AvatarModule,
    DividerModule,
    ButtonModule,
    DatasetVisualizarDocumentosComponent,
    CustomTableComponent,
    NucleoFamiliarHijosComponent
  ],
  templateUrl: './perfil-sisben.component.html',
  styles: `.puntaje {
    background-color: lightblue;
    color: white;
    padding: 1rem 1.5rem;
    border-radius: 10px;
    font-size: 2.5rem;
    max-width: 10rem;
    font-weight: bold;
  }
  `
})
export class PerfilSisbenComponent implements OnInit, OnDestroy {
  //#region Properties
  perfil: IGetPerfilUsuario | null = null;
  nucleo: IGetNucleoFamiliar[] = [];
  loading = true;
  trans: any = {};
  customTableNucleo!: ICustomTable;
  private destroy$ = new Subject<void>();
  @ViewChild(DatasetVisualizarDocumentosComponent) visualizarDocumentos!: DatasetVisualizarDocumentosComponent;
  @ViewChild(CustomTableComponent) customTableComponent!: CustomTableComponent;

  puntajeSisben: string | null = null;
  //#endregion

  constructor(
    private readonly route: ActivatedRoute,
    private readonly sisbenService: SisbenConsultaService,
    private readonly sisbenNucleoFamiliarService: SisbenNucleoFamiliarService,
    private readonly translateService: TranslateService
  ) { }

  //#region Lifecycle
  ngOnInit(): void {
    this.translate();
    this.route.paramMap
      .pipe(takeUntil(this.destroy$))
      .subscribe(params => {
        const sisbenId = params.get('sisbenId');
        if (sisbenId) {
          const sisbenIdNumber = Number(sisbenId);
          this.loadPerfil(sisbenIdNumber);
          this.loadPuntajeSisben(sisbenIdNumber);
        }
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
  //#endregion

  //#region Data Loading
  private async translate(): Promise<void> {
    this.trans = await firstValueFrom(forkJoin({
      nombreCompleto: this.translateService.get('common.nombreCompleto'),
      tipoIdentificacion: this.translateService.get('common.tipoIdentificacion'),
      identificacion: this.translateService.get('common.identificacion'),
      jefeHogar: this.translateService.get('common.jefeHogar'),
      si: this.translateService.get('common.si'),
      no: this.translateService.get('common.no'),
      abrir: this.translateService.get('common.abrir'),
    }));
  }

  private loadPerfil(id: number): void {
    this.loading = true;

    this.sisbenService.getPerfilUsuario(id)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (data) => {
          this.perfil = data;
          this.loadNucleo(id);
        }
      });
  }

  private loadNucleo(id: number): void {
    this.sisbenService.getNucleoFamiliar(id)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (data) => {
          this.nucleo = data;
          this.configureNucleoTable();
          this.loading = false;
        }
      });
  }

  private loadPuntajeSisben(id: number): void {
    this.sisbenNucleoFamiliarService.getPuntajeSisben(id)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (data) => {
          this.puntajeSisben = data.puntajeSisben;
        }
      });
  }

  private configureNucleoTable(): void {
    const padres = this.nucleo.filter(m => m.jefeHogar);
    const hijos = this.nucleo.filter(m => !m.jefeHogar);

    // Tabla de Padres
    this.customTableNucleo = {
      settings: {
        table: {
          paginator: false
        }
      },
      columns: [
        {
          field: 'nombreCompleto',
          header: this.trans.nombreCompleto,
          dataType: DataColumnType.TEXT,
          class: 'font-13',
          columnmHeader: {
            class: 'text-left color-blue-2 font-13 font-bold'
          }
        },
        {
          field: 'tipoIdentificacion',
          header: this.trans.tipoIdentificacion,
          dataType: DataColumnType.TEXT,
          class: 'font-13',
          columnmHeader: {
            class: 'text-left color-blue-2 font-13 font-bold'
          }
        },
        {
          field: 'identificacion',
          header: this.trans.identificacion,
          dataType: DataColumnType.TEXT,
          class: 'font-13',
          columnmHeader: {
            class: 'text-left color-blue-2 font-13 font-bold'
          }
        },
        {
          field: 'abrir',
          header: '',
          label: this.trans.abrir,
          icon: 'pi pi-folder-open',
          class: 'button-edit p-button-outlined pt-2 pb-2',
          width: '0rem',
          buttonType: ButtonColumnType.CUSTOM,
          onClick: (event: Event, data: IGetNucleoFamiliar) => this.openProfile(data)
        }
      ],
    } as ICustomTable;

    const tablaDatos = padres.map(padre => ({
      ...padre,
      hijos: hijos
    }));

    setTimeout(() => {
      this.customTableComponent.data = tablaDatos;
      const firstRow = tablaDatos[0];
      if (firstRow) {
        this.customTableComponent.expandedRowKeys = { [firstRow.id]: true };
      }
    }, 1);
  }
  //#endregion

  //#region Getters
  get nombreCompleto(): string {
    return this.perfil?.nombreCompleto || '';
  }

  get firstLetterName(): string {
    const nombre = this.nombreCompleto;
    return nombre.charAt(0).toUpperCase();
  }
  //#endregion

  //#region Documents
  openDocuments(): void {
    if (!this.perfil) {
      return;
    }
    const column = JSONParser.parse(this.perfil.documentoIdentificacion);
    this.visualizarDocumentos.openModal(this.perfil.tablaConfigId, this.perfil.id, 'documento_identificacion', column);
  }

  private openProfile(data: IGetNucleoFamiliar): void {
    window.open(`#/sisben/perfil/${data.id}`, '_blank');
  }
  //#endregion
}
